import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { BookOpen, ExternalLink, Zap, Network, Shield, Building } from "lucide-react";
import api from "@/lib/api";

const ICONS = {
  electrical: Zap,
  low_voltage: Network,
  safety: Shield,
  regulatory: Building,
};

const CATEGORY_LABEL = {
  electrical: "Electrical",
  low_voltage: "Low Voltage",
  safety: "Safety",
  regulatory: "Regulatory",
};

const COLORS = {
  electrical: { bg: "bg-amber-50", border: "border-amber-200", text: "text-amber-900", icon: "text-amber-700" },
  low_voltage: { bg: "bg-[#003366]/5", border: "border-[#003366]/20", text: "text-[#003366]", icon: "text-[#003366]" },
  safety: { bg: "bg-red-50", border: "border-red-200", text: "text-red-900", icon: "text-red-700" },
  regulatory: { bg: "bg-slate-100", border: "border-slate-300", text: "text-slate-900", icon: "text-slate-700" },
};

export default function References() {
  const { t, i18n } = useTranslation();
  const [refs, setRefs] = useState([]);
  const [filter, setFilter] = useState("all");
  const [expanded, setExpanded] = useState(null);
  const es = i18n.language?.startsWith("es");

  useEffect(() => {
    api.get("/references").then(({ data }) => setRefs(data || []));
  }, []);

  const categories = Array.from(new Set(refs.map((r) => r.category)));
  const filtered = filter === "all" ? refs : refs.filter((r) => r.category === filter);

  return (
    <div className="p-6" data-testid="references-page">
      <header className="mb-6">
        <div className="text-xs font-mono uppercase tracking-[0.2em] text-slate-500">
          IQES · IQ-APP · {es ? "Biblioteca Técnica" : "Technical Library"}
        </div>
        <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900 mt-1">
          {es ? "Referencias Técnicas" : "Technical References"}
        </h1>
        <p className="text-sm text-slate-600 mt-1 max-w-2xl">
          {es
            ? "Resumen curado de normativas que aplican al trabajo de IQES — NEC, TIA, BICSI, OSHA, NFPA, FL DBPR."
            : "Curated summary of regulations applicable to IQES work — NEC, TIA, BICSI, OSHA, NFPA, FL DBPR."}
        </p>
      </header>

      <div className="flex flex-wrap gap-2 mb-4" data-testid="ref-filters">
        <button
          data-testid="ref-filter-all"
          onClick={() => setFilter("all")}
          className={`text-xs font-mono uppercase tracking-wider px-3 py-1.5 rounded-xl border transition-colors ${
            filter === "all" ? "bg-[#003366] text-white border-[#003366]" : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"
          }`}
        >
          All · {refs.length}
        </button>
        {categories.map((c) => (
          <button
            key={c}
            data-testid={`ref-filter-${c}`}
            onClick={() => setFilter(c)}
            className={`text-xs font-mono uppercase tracking-wider px-3 py-1.5 rounded-xl border transition-colors ${
              filter === c ? "bg-[#003366] text-white border-[#003366]" : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"
            }`}
          >
            {CATEGORY_LABEL[c] || c} · {refs.filter((r) => r.category === c).length}
          </button>
        ))}
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {filtered.map((r) => {
          const Icon = ICONS[r.category] || BookOpen;
          const color = COLORS[r.category] || COLORS.regulatory;
          const isOpen = expanded === r.id;
          return (
            <div
              key={r.id}
              data-testid={`ref-card-${r.id}`}
              className={`bg-white border rounded-xl overflow-hidden hover:shadow-md transition-shadow ${color.border}`}
            >
              <button
                data-testid={`ref-toggle-${r.id}`}
                onClick={() => setExpanded(isOpen ? null : r.id)}
                className="w-full text-left p-4"
              >
                <div className="flex items-start gap-3">
                  <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${color.bg} ${color.icon}`}>
                    <Icon size={18} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className={`text-[10px] font-mono uppercase tracking-wider ${color.text}`}>
                      {r.code}
                    </div>
                    <h3 className="font-display font-semibold text-slate-900 mt-0.5 text-base leading-tight">
                      {r.title}
                    </h3>
                    <p className="text-xs text-slate-500 mt-1">{r.scope}</p>
                  </div>
                </div>
                <p className="mt-3 text-sm text-slate-700 leading-relaxed">{r.summary}</p>
              </button>
              {isOpen && (
                <div className="border-t border-slate-100 bg-slate-50/50 p-4 space-y-3">
                  {r.key_articles?.length > 0 && (
                    <div>
                      <div className="text-[10px] font-mono uppercase tracking-wider text-slate-500 mb-2">
                        Key articles / clauses
                      </div>
                      <ul className="space-y-1.5 text-sm">
                        {r.key_articles.map((a, i) => (
                          <li key={i} className="flex gap-2">
                            <span className="font-mono text-xs font-semibold text-[#003366] shrink-0 min-w-[80px]">
                              {a.art}
                            </span>
                            <span className="text-slate-700">{a.topic}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {r.applies_to_iqes && (
                    <div className="border-t border-slate-200 pt-3">
                      <div className="text-[10px] font-mono uppercase tracking-wider text-[#EA580C] mb-1">
                        {es ? "Relevancia para IQES" : "Relevance to IQES"}
                      </div>
                      <p className="text-sm text-slate-800">{r.applies_to_iqes}</p>
                    </div>
                  )}
                  {r.url && (
                    <a
                      href={r.url} target="_blank" rel="noreferrer"
                      className="inline-flex items-center gap-1 text-xs font-mono uppercase tracking-wider text-[#003366] hover:underline"
                    >
                      <ExternalLink size={10} /> Official source
                    </a>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
